/*
  Miamon — carrusel de testimonios.

  Bucle infinito por clonación: detrás de los testimonios reales se
  añaden juegos idénticos, de forma que la posición `count` enseña
  exactamente lo mismo que la 0. Al llegar allí se vuelve al índice
  canónico sin transición y el salto no se ve.

  El carril se mueve con `transform`, no con scroll: así el paso es
  siempre una tarjeta exacta y el reposicionamiento silencioso es
  posible (con scrollLeft el navegador lo delataría con un tirón).

  Custom element para que conviva con el editor de temas, que reinyecta
  secciones sin recargar la página.
*/
if (!customElements.get('mm-tst-carousel')) {
  customElements.define(
    'mm-tst-carousel',
    class MmTstCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.mm-tst__track');
        this.viewport = this.querySelector('.mm-tst__viewport');
        if (!this.track) return;

        this.originales = Array.from(this.track.children);
        this.count = this.originales.length;
        if (this.count < 2) return; // con uno solo no hay nada que rotar

        this.index = 0;
        this.paso = 0;
        this.timer = null;
        this.settle = null;
        this.moviendo = false;
        this.dentro = false;
        this.visible = true;

        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.delay = Number(this.dataset.delay) || 5000;
        this.quiereAuto = this.dataset.autoplay === 'true';

        this.dots = Array.from(this.querySelectorAll('[data-mm-tst-goto]'));
        this.flechas = Array.from(this.querySelectorAll('[data-mm-tst-dir]'));

        this.clonar();
        this.enlazar();
        this.medir();
        this.aplicar(false);
        this.arrancar();

        this.onResize = this.onResize.bind(this);
        window.addEventListener('resize', this.onResize);

        // Rotar testimonios que nadie ve no aporta y gasta batería.
        if ('IntersectionObserver' in window) {
          this.io = new IntersectionObserver(
            (entradas) => {
              entradas.forEach((e) => {
                this.visible = e.isIntersecting;
                this.visible ? this.arrancar() : this.parar();
              });
            },
            { threshold: 0.2 }
          );
          this.io.observe(this);
        }

        this.onVisibility = this.onVisibility.bind(this);
        document.addEventListener('visibilitychange', this.onVisibility);
      }

      disconnectedCallback() {
        this.parar();
        if (this.settle) clearTimeout(this.settle);
        if (this.io) this.io.disconnect();
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('visibilitychange', this.onVisibility);
      }

      onVisibility() {
        document.hidden ? this.parar() : this.arrancar();
      }

      onResize() {
        this.medir();
        this.aplicar(false);
      }

      /* Hacen falta `count` posiciones más las que quepan en pantalla; se
         clonan juegos completos hasta pasarse, que sobre no molesta. */
      clonar() {
        var minimo = this.count + 4;
        while (this.track.children.length < minimo) {
          this.originales.forEach((item) => {
            var copia = item.cloneNode(true);
            copia.setAttribute('aria-hidden', 'true');
            copia.dataset.mmClon = 'true';
            // Sin esto el editor de temas creería que hay bloques de más.
            copia.removeAttribute('data-shopify-editor-block');
            copia.removeAttribute('id');
            this.track.appendChild(copia);
          });
        }
      }

      medir() {
        var card = this.originales[0];
        if (!card) return;
        var gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        this.paso = card.getBoundingClientRect().width + gap;

        var dur = getComputedStyle(this.track).getPropertyValue('--tst-duracion');
        this.duracion = parseFloat(dur) || 620;
        if (dur.indexOf('ms') === -1 && dur.indexOf('s') !== -1) this.duracion *= 1000;
      }

      aplicar(animar) {
        if (!animar) this.track.style.transition = 'none';
        this.track.style.transform = 'translateX(' + -this.index * this.paso + 'px)';
        if (!animar) {
          void this.track.offsetWidth; // fuerza el reflujo antes de devolver la transición
          this.track.style.transition = '';
        }
        this.sincronizarDots();
      }

      sincronizarDots() {
        var actual = ((this.index % this.count) + this.count) % this.count;
        this.dots.forEach((dot, i) => {
          dot.setAttribute('aria-current', i === actual ? 'true' : 'false');
        });
      }

      ir(destino) {
        if (this.moviendo) return;
        if (destino === this.index) return;

        if (destino < 0) {
          // Retroceder desde el principio: primero se salta al juego clonado
          // equivalente (invisible, se ve igual) y desde ahí sí se anima.
          this.index += this.count;
          this.aplicar(false);
          var self = this;
          var objetivo = destino + this.count;
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              self.animarA(objetivo);
            });
          });
          return;
        }

        this.animarA(destino);
      }

      animarA(destino) {
        this.moviendo = true;
        this.index = destino;
        this.aplicar(true);

        if (this.settle) clearTimeout(this.settle);
        this.settle = setTimeout(() => {
          this.settle = null;
          this.moviendo = false;
          // De vuelta al rango canónico: la posición `count` enseña lo mismo
          // que la 0, así que el reajuste sin transición no se percibe.
          if (this.index >= this.count) {
            this.index -= this.count;
            this.aplicar(false);
          }
        }, this.duracion + 40);
      }

      siguiente() {
        this.ir(this.index + 1);
      }

      anterior() {
        this.ir(this.index - 1);
      }

      enlazar() {
        this.flechas.forEach((btn) => {
          btn.addEventListener('click', () => {
            Number(btn.dataset.mmTstDir) > 0 ? this.siguiente() : this.anterior();
            this.reiniciarAuto();
          });
        });

        this.dots.forEach((dot, i) => {
          dot.addEventListener('click', () => {
            this.ir(i);
            this.reiniciarAuto();
          });
        });

        // El puntero encima congela el ciclo: leer una cita no debería ser
        // una carrera contra el temporizador.
        this.addEventListener('pointerover', (event) => {
          if (event.pointerType === 'touch') return;
          this.dentro = true;
          this.parar();
        });
        this.addEventListener('pointerout', (event) => {
          if (event.pointerType === 'touch') return;
          if (event.relatedTarget && this.contains(event.relatedTarget)) return;
          this.dentro = false;
          this.arrancar();
        });

        this.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') this.siguiente();
          if (event.key === 'ArrowLeft') this.anterior();
        });

        this.enlazarArrastre();
      }

      enlazarArrastre() {
        // Gesto compartido con el hero y los reels (assets/mm-drag.js).
        if (!this.viewport || !window.mmArrastre) return;
        window.mmArrastre(this.viewport, (dir) => {
          dir > 0 ? this.siguiente() : this.anterior();
          this.reiniciarAuto();
        });
      }

      arrancar() {
        if (!this.quiereAuto || this.reduced) return;
        if (this.dentro || !this.visible || document.hidden) return;
        this.parar();
        this.timer = setInterval(this.siguiente.bind(this), this.delay);
      }

      parar() {
        if (this.timer) clearInterval(this.timer);
        this.timer = null;
      }

      /* Tras una interacción manual, el ciclo vuelve a contar desde cero. */
      reiniciarAuto() {
        if (!this.timer) return;
        this.arrancar();
      }
    }
  );
}
